import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AddressSelectorComponent } from '../../shared/design-system/address-selector.component';
import {
  CreatePlatformPartyPayload,
  FinanceApiService,
  PartyLinkedCustomerRecord,
  PartyAddressRecord,
  PartyAddressValue,
  PlatformPartyComplianceProfileRecord,
  PlatformPartyDetailRecord,
  PlatformPartyRelationRecord,
  UpsertPartyAddressPayload,
  UpdatePlatformCustomerComplianceProfilePayload,
  UpdatePlatformPartyPayload
} from '../finance/finance-api.service';

type KindOption = { value: number; labelKey: string };
type IdentifierTypeOption = { value: number; labelKey: string };
type ContactTypeOption = { value: number; labelKey: string };
type RelationTypeOption = { value: number; labelKey: string };
type ComplianceStatusOption = { value: number; labelKey: string };

@Component({
  standalone: true,
  selector: 'ain-platform-party-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    TableModule,
    TabsModule,
    InfoHintComponent,
    PartySelectorComponent,
    AddressSelectorComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="isCreateMode() ? t('platform.parties.form.createTitle') : t('platform.parties.form.editTitle')"
      [description]="t('platform.parties.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="isCreateMode() ? 'partyHub.help.partyCreate.title' : 'partyHub.help.partyDetail.title'"
        [descriptionKey]="isCreateMode() ? 'partyHub.help.partyCreate.description' : 'partyHub.help.partyDetail.description'"
        [bulletKeys]="isCreateMode() ? [
          'partyHub.help.partyCreate.bullets.kind',
          'partyHub.help.partyCreate.bullets.inlineContact',
          'partyHub.help.partyCreate.bullets.afterSave'
        ] : [
          'partyHub.help.partyDetail.bullets.compliance',
          'partyHub.help.partyDetail.bullets.customers',
          'partyHub.help.partyDetail.bullets.relations'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/platform/parties']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="saving()"></button>
        </div>

        @if (isCreateMode()) {
          <section class="card">
            <div class="grid">
              <label>
                <span>{{ t('platform.parties.fields.displayName') }}</span>
                <input pInputText [(ngModel)]="form.displayName" />
              </label>
              <label>
                <span>{{ t('platform.parties.fields.legalName') }}</span>
                <input pInputText [(ngModel)]="form.legalName" />
              </label>
              <label>
                <span>{{ t('platform.parties.fields.kind') }}</span>
                <p-select class="field-select" [options]="kindOptions" optionValue="value" [(ngModel)]="form.kind" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </section>

          <section class="card">
            <div class="section-header">
              <h3>{{ t('partyHub.help.partyCreateInline.title') }}</h3>
              <p>{{ t('partyHub.help.partyCreateInline.description') }}</p>
            </div>
            <div class="grid">
              <label>
                <span>{{ t('platform.parties.identifiers.type') }}</span>
                <p-select class="field-select" [options]="identifierTypeOptions" optionValue="value" [(ngModel)]="identifierForm.identifierType" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('platform.parties.identifiers.value') }}</span>
                <input pInputText [(ngModel)]="identifierForm.value" />
              </label>
              <label>
                <span>{{ t('platform.parties.contactPoints.type') }}</span>
                <p-select class="field-select" [options]="contactTypeOptions" optionValue="value" [(ngModel)]="contactForm.contactType" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('platform.parties.contactPoints.value') }}</span>
                <input pInputText [(ngModel)]="contactForm.value" />
              </label>
            </div>
          </section>
        } @else if (currentParty()) {
          <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
            <p-tablist>
              <p-tab value="profile">{{ t('partyHub.tabs.profile') }}</p-tab>
              <p-tab value="compliance">{{ t('partyHub.tabs.compliance') }}</p-tab>
              <p-tab value="identifiers">{{ t('partyHub.tabs.identifiers') }}</p-tab>
              <p-tab value="contacts">{{ t('partyHub.tabs.contacts') }}</p-tab>
              <p-tab value="addresses">{{ t('partyHub.tabs.addresses') }}</p-tab>
              <p-tab value="relations">{{ t('partyHub.tabs.relations') }}</p-tab>
              <p-tab value="customers">{{ t('partyHub.tabs.customers') }}</p-tab>
            </p-tablist>
            <p-tabpanels>
              <p-tabpanel value="profile">
                <section class="card">
                  <div class="grid">
                    <label>
                      <span>{{ t('platform.parties.fields.displayName') }}</span>
                      <input pInputText [(ngModel)]="form.displayName" />
                    </label>
                    <label>
                      <span>{{ t('platform.parties.fields.legalName') }}</span>
                      <input pInputText [(ngModel)]="form.legalName" />
                    </label>
                    <label>
                      <span>{{ t('platform.parties.fields.kind') }}</span>
                      <p-select class="field-select" [options]="kindOptions" optionValue="value" [(ngModel)]="form.kind" appendTo="body">
                        <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                        <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                      </p-select>
                    </label>
                    <label>
                      <span>{{ t('columns.status') }}</span>
                      <input pInputText [value]="statusLabel(currentParty()?.status)" readonly />
                    </label>
                  </div>
                </section>
              </p-tabpanel>

              <p-tabpanel value="compliance">
          <section class="card">
            <div class="section-header">
              <h3>{{ t('finance.customers.compliance.title') }}</h3>
              <p>{{ t('finance.customers.compliance.description') }}</p>
            </div>
            <p class="gate-hint" [class.allowed]="complianceProfile()?.allowsRegulatedOperation">
              {{
                complianceProfile()?.allowsRegulatedOperation
                  ? t('finance.customers.compliance.regulatedAllowed')
                  : t('finance.customers.compliance.regulatedBlocked')
              }}
            </p>
            @if (complianceProfile()?.hasActiveReview) {
              <p class="review-pending">
                {{ t('finance.customers.compliance.reviewPending') }}
                @if (complianceProfile()?.pendingReviewProcessInstanceId) {
                  <a [routerLink]="['/workflow/tasks']">
                    {{ t('finance.customers.compliance.openWorkflowTasks') }}
                  </a>
                }
              </p>
            }
            <div class="grid">
              @for (field of complianceFields; track field.key) {
                <label>
                  <span>{{ t(field.labelKey) }}</span>
                  <p-select
                    class="field-select"
                    [options]="complianceStatusOptions"
                    optionValue="value"
                    [(ngModel)]="complianceForm[field.key]"
                    appendTo="body">
                    <ng-template pTemplate="selectedItem" let-item>
                      {{ item ? t(item.labelKey) : '-' }}
                    </ng-template>
                    <ng-template pTemplate="item" let-item>
                      {{ t(item.labelKey) }}
                    </ng-template>
                  </p-select>
                </label>
              }
              <label class="full">
                <span>{{ t('finance.customers.compliance.reviewNotes') }}</span>
                <input pInputText [(ngModel)]="complianceForm.reviewNotes" />
              </label>
            </div>
            <button
              pButton
              type="button"
              size="small"
              [label]="t('finance.customers.compliance.save')"
              (click)="saveCompliance()"
              [disabled]="complianceSaving() || complianceProfile()?.hasActiveReview">
            </button>
            <button
              pButton
              type="button"
              size="small"
              severity="secondary"
              [label]="t('finance.customers.compliance.submitReview')"
              (click)="submitComplianceReview()"
              [disabled]="complianceSaving() || complianceProfile()?.hasActiveReview">
            </button>
          </section>
              </p-tabpanel>

              <p-tabpanel value="identifiers">
          <section class="card">
            <div class="section-header">
              <h3>{{ t('platform.parties.identifiers.title') }}</h3>
              <button pButton type="button" size="small" [label]="t('platform.parties.identifiers.add')" (click)="addIdentifier()"></button>
            </div>
            <div class="grid">
              <label>
                <span>{{ t('platform.parties.identifiers.type') }}</span>
                <p-select
                  class="field-select"
                  [options]="identifierTypeOptions"
                  optionValue="value"
                  [(ngModel)]="identifierForm.identifierType"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>
                    {{ item ? t(item.labelKey) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-item>
                    {{ t(item.labelKey) }}
                  </ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('platform.parties.identifiers.value') }}</span>
                <input pInputText [(ngModel)]="identifierForm.value" />
              </label>
              <label>
                <span>{{ t('platform.parties.identifiers.country') }}</span>
                <input pInputText [(ngModel)]="identifierForm.issuingCountryCode" />
              </label>
              <label class="checkbox-row">
                <p-checkbox binary="true" [(ngModel)]="identifierForm.isPrimary" />
                <span>{{ t('platform.parties.identifiers.primary') }}</span>
              </label>
            </div>
            <p-table [value]="currentParty()!.identifiers">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('platform.parties.identifiers.type') }}</th>
                  <th>{{ t('platform.parties.identifiers.value') }}</th>
                  <th>{{ t('platform.parties.identifiers.country') }}</th>
                  <th>{{ t('platform.parties.identifiers.primary') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ identifierTypeLabel(row.identifierType) }}</td>
                  <td>{{ row.value }}</td>
                  <td>{{ row.issuingCountryCode || '-' }}</td>
                  <td>{{ row.isPrimary ? t('common.yes') : t('common.no') }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
              </p-tabpanel>

              <p-tabpanel value="contacts">
          <section class="card">
            <div class="section-header">
              <h3>{{ t('platform.parties.contactPoints.title') }}</h3>
              <button pButton type="button" size="small" [label]="t('platform.parties.contactPoints.add')" (click)="addContactPoint()"></button>
            </div>
            <div class="grid">
              <label>
                <span>{{ t('platform.parties.contactPoints.type') }}</span>
                <p-select
                  class="field-select"
                  [options]="contactTypeOptions"
                  optionValue="value"
                  [(ngModel)]="contactForm.contactType"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>
                    {{ item ? t(item.labelKey) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-item>
                    {{ t(item.labelKey) }}
                  </ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('platform.parties.contactPoints.value') }}</span>
                <input pInputText [(ngModel)]="contactForm.value" />
              </label>
              <label class="checkbox-row">
                <p-checkbox binary="true" [(ngModel)]="contactForm.isPrimary" />
                <span>{{ t('platform.parties.contactPoints.primary') }}</span>
              </label>
            </div>
            <p-table [value]="currentParty()!.contactPoints">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('platform.parties.contactPoints.type') }}</th>
                  <th>{{ t('platform.parties.contactPoints.value') }}</th>
                  <th>{{ t('platform.parties.contactPoints.primary') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ contactTypeLabel(row.contactType) }}</td>
                  <td>{{ row.value }}</td>
                  <td>{{ row.isPrimary ? t('common.yes') : t('common.no') }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
              </p-tabpanel>

              <p-tabpanel value="addresses">
          <section class="card">
            <div class="section-header">
              <h3>{{ t('platform.geography.addresses.title') }}</h3>
              <button pButton type="button" size="small" [label]="t('platform.geography.addresses.add')" (click)="saveAddress()"></button>
            </div>
            <ain-address-selector [(ngModel)]="addressForm.value" />
            <label class="checkbox-row">
              <p-checkbox binary="true" [(ngModel)]="addressForm.isPrimary" />
              <span>{{ t('platform.geography.addresses.primary') }}</span>
            </label>
            <p-table [value]="partyAddresses()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('platform.geography.fields.route') }}</th>
                  <th>{{ t('platform.geography.fields.streetLine1') }}</th>
                  <th>{{ t('platform.geography.addresses.primary') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.routeAreaId || row.districtAreaId || row.resolvedAreaId }}</td>
                  <td>{{ row.streetLine1 || '-' }}</td>
                  <td>{{ row.isPrimary ? t('common.yes') : t('common.no') }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
              </p-tabpanel>

              <p-tabpanel value="relations">
          <section class="card">
            <div class="section-header">
              <h3>{{ t('platform.parties.relations.title') }}</h3>
              <button pButton type="button" size="small" [label]="t('platform.parties.relations.add')" (click)="addRelation()"></button>
            </div>
            <div class="grid">
              <label class="full">
                <span>{{ t('platform.parties.relations.relatedParty') }}</span>
                <ain-party-selector [(ngModel)]="relationForm.toPartyId" />
              </label>
              <label>
                <span>{{ t('platform.parties.relations.type') }}</span>
                <p-select
                  class="field-select"
                  [options]="relationTypeOptions"
                  optionValue="value"
                  [(ngModel)]="relationForm.relationType"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>
                    {{ item ? t(item.labelKey) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-item>
                    {{ t(item.labelKey) }}
                  </ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('platform.parties.relations.effectiveFrom') }}</span>
                <input pInputText [(ngModel)]="relationForm.effectiveFromUtc" />
              </label>
              <label>
                <span>{{ t('platform.parties.relations.effectiveTo') }}</span>
                <input pInputText [(ngModel)]="relationForm.effectiveToUtc" />
              </label>
              <label class="full">
                <span>{{ t('platform.parties.relations.notes') }}</span>
                <input pInputText [(ngModel)]="relationForm.notes" />
              </label>
            </div>
            <p-table [value]="relations()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('platform.parties.relations.direction') }}</th>
                  <th>{{ t('platform.parties.relations.type') }}</th>
                  <th>{{ t('platform.parties.relations.effectiveFrom') }}</th>
                  <th>{{ t('platform.parties.relations.effectiveTo') }}</th>
                  <th>{{ t('platform.parties.relations.notes') }}</th>
                  <th>{{ t('platform.parties.fields.actions') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>
                    <a [routerLink]="['/platform/parties', relatedPartyId(row)]">
                      {{ relatedPartyLabel(row) }}
                    </a>
                  </td>
                  <td>{{ relationTypeLabel(row.relationType) }}</td>
                  <td>{{ row.effectiveFromUtc || '-' }}</td>
                  <td>{{ row.effectiveToUtc || '-' }}</td>
                  <td>{{ row.notes || '-' }}</td>
                  <td>
                    <button
                      pButton
                      type="button"
                      size="small"
                      severity="danger"
                      [label]="t('common.delete')"
                      (click)="deleteRelation(row)">
                    </button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </section>
              </p-tabpanel>

              <p-tabpanel value="customers">
                <section class="card">
                  <div class="section-header">
                    <h3>{{ t('partyHub.tabs.customers') }}</h3>
                  </div>
                  @if (!linkedCustomers().length) {
                    <p>{{ t('common.noRecords') }}</p>
                  } @else {
                    <p-table [value]="linkedCustomers()">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ t('finance.customers.fields.customerCode') }}</th>
                          <th>{{ t('finance.customers.fields.displayName') }}</th>
                          <th>{{ t('columns.status') }}</th>
                          <th>{{ t('finance.customers.fields.actions') }}</th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-row>
                        <tr>
                          <td>{{ row.customerCode }}</td>
                          <td>{{ row.displayName }}</td>
                          <td>{{ linkedCustomerStatusLabel(row.status) }}</td>
                          <td>
                            <a class="row-link" [routerLink]="['/financial/payments/customers', row.id]">{{ t('common.edit') }}</a>
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  }
                </section>
              </p-tabpanel>
            </p-tabpanels>
          </p-tabs>
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .field-select { width: 100%; }
    .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
    .section-header h3 { margin: 0; font-size: 1rem; }
    .section-header p { margin: 0 0 10px; color: #6b7280; font-size: 0.875rem; }
    .gate-hint { margin: 0 0 10px; font-weight: 600; color: #b91c1c; }
    .gate-hint.allowed { color: #15803d; }
    .review-pending { margin: 0 0 10px; color: #92400e; font-weight: 600; }
    .review-pending a { margin-inline-start: 8px; color: var(--ain-color-primary); }
    label.full { grid-column: 1 / -1; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; align-self: end; }
    .message.error { color: #b91c1c; }
    .row-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class PlatformPartyDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly complianceSaving = signal(false);
  readonly errorMessage = signal('');
  readonly currentParty = signal<PlatformPartyDetailRecord | null>(null);
  readonly partyAddresses = signal<PartyAddressRecord[]>([]);
  readonly complianceProfile = signal<PlatformPartyComplianceProfileRecord | null>(null);
  readonly relations = signal<PlatformPartyRelationRecord[]>([]);
  readonly linkedCustomers = signal<PartyLinkedCustomerRecord[]>([]);
  readonly isCreateMode = signal(false);
  readonly activeTab = signal('profile');

  readonly kindOptions: KindOption[] = [
    { value: 0, labelKey: 'platform.parties.kind.individual' },
    { value: 1, labelKey: 'platform.parties.kind.organization' },
    { value: 2, labelKey: 'platform.parties.kind.device' },
    { value: 3, labelKey: 'platform.parties.kind.aiAgent' },
    { value: 4, labelKey: 'platform.parties.kind.service' }
  ];

  readonly identifierTypeOptions: IdentifierTypeOption[] = [
    { value: 0, labelKey: 'platform.parties.identifierType.nationalId' },
    { value: 1, labelKey: 'platform.parties.identifierType.taxId' },
    { value: 2, labelKey: 'platform.parties.identifierType.registrationNumber' },
    { value: 3, labelKey: 'platform.parties.identifierType.passportNumber' },
    { value: 4, labelKey: 'platform.parties.identifierType.brokerCode' },
    { value: 5, labelKey: 'platform.parties.identifierType.other' }
  ];

  readonly contactTypeOptions: ContactTypeOption[] = [
    { value: 0, labelKey: 'platform.parties.contactType.email' },
    { value: 1, labelKey: 'platform.parties.contactType.phone' },
    { value: 2, labelKey: 'platform.parties.contactType.mobile' },
    { value: 3, labelKey: 'platform.parties.contactType.address' },
    { value: 4, labelKey: 'platform.parties.contactType.other' }
  ];

  readonly relationTypeOptions: RelationTypeOption[] = [
    { value: 0, labelKey: 'platform.parties.relationType.parentChild' },
    { value: 1, labelKey: 'platform.parties.relationType.beneficiary' },
    { value: 2, labelKey: 'platform.parties.relationType.guarantor' },
    { value: 3, labelKey: 'platform.parties.relationType.branchOf' },
    { value: 4, labelKey: 'platform.parties.relationType.managedBy' }
  ];

  readonly complianceStatusOptions: ComplianceStatusOption[] = [
    { value: 0, labelKey: 'finance.customers.compliance.status.notStarted' },
    { value: 1, labelKey: 'finance.customers.compliance.status.pending' },
    { value: 2, labelKey: 'finance.customers.compliance.status.passed' },
    { value: 3, labelKey: 'finance.customers.compliance.status.failed' },
    { value: 4, labelKey: 'finance.customers.compliance.status.waived' }
  ];

  readonly complianceFields = [
    { key: 'kycStatus' as const, labelKey: 'finance.customers.compliance.kycStatus' },
    { key: 'kybStatus' as const, labelKey: 'finance.customers.compliance.kybStatus' },
    { key: 'amlStatus' as const, labelKey: 'finance.customers.compliance.amlStatus' },
    { key: 'sanctionsStatus' as const, labelKey: 'finance.customers.compliance.sanctionsStatus' },
    { key: 'suitabilityStatus' as const, labelKey: 'finance.customers.compliance.suitabilityStatus' }
  ];

  form = {
    displayName: '',
    legalName: '',
    kind: 0
  };

  identifierForm = {
    identifierType: 0,
    value: '',
    issuingCountryCode: '',
    isPrimary: false
  };

  contactForm = {
    contactType: 0,
    value: '',
    isPrimary: false
  };

  addressForm = {
    value: {
      countryAreaId: null,
      provinceAreaId: null,
      cityAreaId: null,
      districtAreaId: null,
      routeAreaId: null,
      streetLine1: null,
      streetLine2: null,
      postalCode: null
    } as PartyAddressValue,
    isPrimary: true
  };

  relationForm = {
    toPartyId: '',
    relationType: 0,
    effectiveFromUtc: '',
    effectiveToUtc: '',
    notes: ''
  };

  complianceForm = {
    kycStatus: 0,
    kybStatus: 0,
    amlStatus: 0,
    sanctionsStatus: 0,
    suitabilityStatus: 0,
    reviewNotes: ''
  };

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const tab = params.get('tab');
      const editTabs = ['profile', 'compliance', 'identifiers', 'contacts', 'addresses', 'relations', 'customers'];
      if (tab && editTabs.includes(tab)) {
        this.activeTab.set(tab);
        const partyId = this.route.snapshot.paramMap.get('partyId');
        if (tab === 'customers' && partyId && partyId !== 'new') {
          this.loadLinkedCustomers(partyId);
        }
      }

      const kindParam = params.get('kind');
      if (kindParam !== null && this.isCreateMode()) {
        const kind = Number(kindParam);
        if (Number.isFinite(kind)) {
          this.form.kind = kind;
        }
      }
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.loadFromRoute(params.get('partyId'));
    });
  }

  private loadFromRoute(partyId: string | null): void {
    const isCreate = !partyId || partyId === 'new';
    this.isCreateMode.set(isCreate);
    this.errorMessage.set('');

    if (isCreate) {
      this.currentParty.set(null);
      this.complianceProfile.set(null);
      this.relations.set([]);
      this.partyAddresses.set([]);
      this.linkedCustomers.set([]);
      this.loading.set(false);
      return;
    }

    this.loadParty(partyId);
  }

  onTabChange(tab: string | number | undefined): void {
    if (tab === undefined || tab === null) {
      return;
    }

    const key = String(tab);
    this.activeTab.set(key);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: key },
      queryParamsHandling: 'merge'
    });

    const current = this.currentParty();
    if (key === 'customers' && current) {
      this.loadLinkedCustomers(current.id);
    }
  }

  save(): void {
    const displayName = this.form.displayName.trim();
    if (!displayName) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');

    if (this.isCreateMode()) {
      const payload: CreatePlatformPartyPayload = {
        displayName,
        kind: this.form.kind,
        legalName: this.form.legalName.trim() || undefined
      };
      this.api.createPlatformParty(payload).subscribe({
        next: created => {
          this.finishCreateParty(created.id);
        },
        error: () => {
          this.errorMessage.set(this.t('common.saveFailed'));
          this.saving.set(false);
        }
      });
      return;
    }

    const current = this.currentParty();
    if (!current) {
      this.errorMessage.set(this.t('common.saveFailed'));
      this.saving.set(false);
      return;
    }

    const payload: UpdatePlatformPartyPayload = {
      displayName,
      kind: this.form.kind,
      legalName: this.form.legalName.trim() || undefined
    };
    this.api.updatePlatformParty(current.id, payload).subscribe({
      next: updated => {
        this.currentParty.update(party => party ? { ...party, ...updated } : party);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  saveCompliance(): void {
    const current = this.currentParty();
    if (!current) {
      return;
    }

    this.complianceSaving.set(true);
    this.errorMessage.set('');

    const payload: UpdatePlatformCustomerComplianceProfilePayload = {
      kycStatus: this.complianceForm.kycStatus,
      kybStatus: this.complianceForm.kybStatus,
      amlStatus: this.complianceForm.amlStatus,
      sanctionsStatus: this.complianceForm.sanctionsStatus,
      suitabilityStatus: this.complianceForm.suitabilityStatus,
      reviewNotes: this.complianceForm.reviewNotes.trim() || undefined
    };

    this.api.updatePlatformPartyComplianceProfile(current.id, payload).subscribe({
      next: profile => {
        this.complianceProfile.set(profile);
        this.complianceSaving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.complianceSaving.set(false);
      }
    });
  }

  submitComplianceReview(): void {
    const current = this.currentParty();
    if (!current) {
      return;
    }

    this.complianceSaving.set(true);
    this.errorMessage.set('');

    const payload: UpdatePlatformCustomerComplianceProfilePayload = {
      kycStatus: this.complianceForm.kycStatus,
      kybStatus: this.complianceForm.kybStatus,
      amlStatus: this.complianceForm.amlStatus,
      sanctionsStatus: this.complianceForm.sanctionsStatus,
      suitabilityStatus: this.complianceForm.suitabilityStatus,
      reviewNotes: this.complianceForm.reviewNotes.trim() || undefined
    };

    this.api.submitPlatformPartyComplianceReview(current.id, payload).subscribe({
      next: profile => {
        this.complianceProfile.set(profile);
        this.complianceSaving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.customers.compliance.submitReviewFailed'));
        this.complianceSaving.set(false);
      }
    });
  }

  addIdentifier(): void {
    const current = this.currentParty();
    const value = this.identifierForm.value.trim();
    if (!current || !value) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    this.api.addPlatformPartyIdentifier(current.id, {
      identifierType: this.identifierForm.identifierType,
      value,
      issuingCountryCode: this.identifierForm.issuingCountryCode.trim() || undefined,
      isPrimary: this.identifierForm.isPrimary
    }).subscribe({
      next: () => {
        this.identifierForm.value = '';
        this.identifierForm.issuingCountryCode = '';
        this.identifierForm.isPrimary = false;
        this.loadParty(current.id);
      },
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  addContactPoint(): void {
    const current = this.currentParty();
    const value = this.contactForm.value.trim();
    if (!current || !value) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    this.api.addPlatformPartyContactPoint(current.id, {
      contactType: this.contactForm.contactType,
      value,
      isPrimary: this.contactForm.isPrimary
    }).subscribe({
      next: () => {
        this.contactForm.value = '';
        this.contactForm.isPrimary = false;
        this.loadParty(current.id);
      },
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  addRelation(): void {
    const current = this.currentParty();
    const toPartyId = this.relationForm.toPartyId.trim();
    if (!current || !toPartyId) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    if (toPartyId === current.id) {
      this.errorMessage.set(this.t('platform.parties.relations.selfNotAllowed'));
      return;
    }

    this.api.createPlatformPartyRelation(current.id, {
      toPartyId,
      relationType: this.relationForm.relationType,
      effectiveFromUtc: this.relationForm.effectiveFromUtc.trim() || undefined,
      effectiveToUtc: this.relationForm.effectiveToUtc.trim() || undefined,
      notes: this.relationForm.notes.trim() || undefined
    }).subscribe({
      next: () => {
        this.relationForm = {
          toPartyId: '',
          relationType: 0,
          effectiveFromUtc: '',
          effectiveToUtc: '',
          notes: ''
        };
        this.loadRelations(current.id);
      },
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  deleteRelation(row: PlatformPartyRelationRecord): void {
    const current = this.currentParty();
    if (!current) {
      return;
    }

    this.api.deletePlatformPartyRelation(current.id, row.id).subscribe({
      next: () => this.loadRelations(current.id),
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  relatedPartyId(row: PlatformPartyRelationRecord): string {
    const current = this.currentParty();
    if (!current) {
      return row.toPartyId;
    }

    return row.fromPartyId === current.id ? row.toPartyId : row.fromPartyId;
  }

  relatedPartyLabel(row: PlatformPartyRelationRecord): string {
    const current = this.currentParty();
    if (!current) {
      return row.toPartyDisplayName;
    }

    if (row.fromPartyId === current.id) {
      return `${this.t('platform.parties.relations.to')}: ${row.toPartyDisplayName}`;
    }

    return `${this.t('platform.parties.relations.from')}: ${row.fromPartyDisplayName}`;
  }

  relationTypeLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const option = this.relationTypeOptions.find(item => item.value === numeric);
    return option ? this.t(option.labelKey) : `${value}`;
  }

  statusLabel(value: number | string | undefined): string {
    if (typeof value === 'undefined') {
      return '-';
    }

    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('platform.parties.status.active'),
      1: this.t('platform.parties.status.inactive'),
      2: this.t('platform.parties.status.merged')
    };
    return map[numeric] ?? `${value}`;
  }

  identifierTypeLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const option = this.identifierTypeOptions.find(item => item.value === numeric);
    return option ? this.t(option.labelKey) : `${value}`;
  }

  contactTypeLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const option = this.contactTypeOptions.find(item => item.value === numeric);
    return option ? this.t(option.labelKey) : `${value}`;
  }

  linkedCustomerStatusLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('finance.customers.status.draft'),
      1: this.t('finance.customers.status.active'),
      2: this.t('finance.customers.status.suspended'),
      3: this.t('finance.customers.status.closed')
    };
    return map[numeric] ?? `${value}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private finishCreateParty(partyId: string): void {
    const identifierValue = this.identifierForm.value.trim();
    const contactValue = this.contactForm.value.trim();

    if (!identifierValue && !contactValue) {
      this.saving.set(false);
      void this.router.navigate(['/platform/parties', partyId]);
      return;
    }

    const afterExtras = (): void => {
      this.saving.set(false);
      void this.router.navigate(['/platform/parties', partyId]);
    };

    if (identifierValue) {
      this.api.addPlatformPartyIdentifier(partyId, {
        identifierType: this.identifierForm.identifierType,
        value: identifierValue,
        issuingCountryCode: this.identifierForm.issuingCountryCode.trim() || undefined,
        isPrimary: true
      }).subscribe({
        next: () => {
          if (contactValue) {
            this.api.addPlatformPartyContactPoint(partyId, {
              contactType: this.contactForm.contactType,
              value: contactValue,
              isPrimary: true
            }).subscribe({
              next: () => afterExtras(),
              error: () => afterExtras()
            });
            return;
          }

          afterExtras();
        },
        error: () => afterExtras()
      });
      return;
    }

    this.api.addPlatformPartyContactPoint(partyId, {
      contactType: this.contactForm.contactType,
      value: contactValue,
      isPrimary: true
    }).subscribe({
      next: () => afterExtras(),
      error: () => afterExtras()
    });
  }

  private loadLinkedCustomers(partyId: string): void {
    this.api.listPartyLinkedCustomers(partyId).subscribe({
      next: rows => this.linkedCustomers.set(rows),
      error: () => this.linkedCustomers.set([])
    });
  }

  saveAddress(): void {
    const current = this.currentParty();
    if (!current || !this.addressForm.value.countryAreaId) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    const payload: UpsertPartyAddressPayload = {
      ...this.addressForm.value,
      isPrimary: this.addressForm.isPrimary
    };
    this.api.createPartyAddress(current.id, payload).subscribe({
      next: () => {
        this.loadAddresses(current.id);
        this.addressForm.value = {
          countryAreaId: null,
          provinceAreaId: null,
          cityAreaId: null,
          districtAreaId: null,
          routeAreaId: null,
          streetLine1: null,
          streetLine2: null,
          postalCode: null
        };
      },
      error: () => this.errorMessage.set(this.t('common.saveFailed'))
    });
  }

  private loadAddresses(partyId: string): void {
    this.api.listPartyAddresses(partyId).subscribe({
      next: rows => this.partyAddresses.set(rows),
      error: () => this.partyAddresses.set([])
    });
  }

  private loadParty(partyId: string): void {
    this.loading.set(true);
    this.api.getPlatformParty(partyId).subscribe({
      next: party => {
        this.currentParty.set(party);
        this.form = {
          displayName: party.displayName,
          legalName: party.legalName ?? '',
          kind: this.normalizeNumeric(party.kind)
        };
        this.loadComplianceProfile(party.id);
        this.loadRelations(party.id);
        this.loadAddresses(party.id);
        if (this.activeTab() === 'customers') {
          this.loadLinkedCustomers(party.id);
        }
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  private loadComplianceProfile(partyId: string): void {
    this.api.getPlatformPartyComplianceProfile(partyId).subscribe({
      next: profile => {
        this.complianceProfile.set(profile);
        this.complianceForm = {
          kycStatus: this.normalizeNumeric(profile.kycStatus),
          kybStatus: this.normalizeNumeric(profile.kybStatus),
          amlStatus: this.normalizeNumeric(profile.amlStatus),
          sanctionsStatus: this.normalizeNumeric(profile.sanctionsStatus),
          suitabilityStatus: this.normalizeNumeric(profile.suitabilityStatus),
          reviewNotes: profile.reviewNotes ?? ''
        };
      },
      error: () => this.complianceProfile.set(null)
    });
  }

  private loadRelations(partyId: string): void {
    this.api.listPlatformPartyRelations(partyId).subscribe({
      next: items => this.relations.set(items),
      error: () => this.relations.set([])
    });
  }

  private normalizeNumeric(value: number | string): number {
    return typeof value === 'string' ? Number(value) : value;
  }
}
